import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { Dropdown } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import {
  ItemSelectorBox,
  ItemSelectorLabel,
  ItemSelectorRow,
  ReadonlyField,
  AddCircleButton,
  AddLineButtonWrapper,
  FreeLineLink,
  ErrorText,
  ContainerLabel,
} from "../../AddEditPurchaseOrder.styled";
import {
  labelStyles,
  inputStyles,
  autoSearchInputStyles,
} from "@utils/extra-style";

const PurchaseOrderItemSelector = ({
  t,
  context,
  supplierId,
  siteId,
  siteName,
  siteOpportunityNumber,
  searchCountryId,
  selectedSite,
  setSelectedSite,
  onAddLine,
  onAddFreeLine,
}) => {
  const [siteQuery, setSiteQuery] = useState();
  const [showSiteError, setShowSiteError] = useState(false);
  const [debouncedSiteQuery] = useDebounce(siteQuery, 700);
  const [siteOptions, setSiteOptions] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  const [quoteServices, setQuoteServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState("");
  const [selectedImpactedService, setSelectedImpactedService] = useState("");

  const effectiveSiteId = context === "site" ? siteId : selectedSite?.id;
  const effectiveSiteName = context === "site" ? siteName : selectedSite?.name;
  const effectiveOpportunityNumber =
    context === "site" ? siteOpportunityNumber : selectedSite?.opportunityNumber;

  const searchSites = () => {
    if (!searchCountryId) {
      setSiteOptions([]);
      return;
    }
    setLoadingSites(true);
    const searchParam = debouncedSiteQuery
      ? `&search=${debouncedSiteQuery}`
      : "";
    axios
      .get(
        `/sites?countries=[${searchCountryId}]&limit=20${searchParam}&statusesId=[3,12,13,14,15]`
      )
      .then((response) => {
        setSiteOptions(response.data?.sites || []);
      })
      .catch(() => setSiteOptions([]))
      .finally(() => setLoadingSites(false));
  };

  useEffect(() => {
    if (context === "company") {
      searchSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSiteQuery]);

  const normalizeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.services)) return data.services;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.records)) return data.records;
    return [];
  };

  const fetchServices = () => {
    if (!effectiveSiteId) {
      setQuoteServices([]);
      return;
    }
    setLoadingServices(true);
    axios
      .get(`/sites/${effectiveSiteId}/services?status=1`)
      .then((response) => {
        setQuoteServices(normalizeArray(response.data));
      })
      .catch(() => setQuoteServices([]))
      .finally(() => setLoadingServices(false));
  };

  useEffect(() => {
    setSelectedServiceGroup("");
    setSelectedImpactedService(null);
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSiteId]);

  const serviceGroupOptions = quoteServices.map((qs) => ({
    value: qs.id,
    label: qs.label,
  }));

  const currentGroup = quoteServices.find(
    (qs) => qs.id === selectedServiceGroup?.value
  );

  const impactedServiceOptions = (
    currentGroup?.quoteitems || currentGroup?.items || []
  ).map((item) => ({
    value: item.id,
    label: `${item.label || item.hesusServiceName || ""} (${
      item.sellingPrice?.toFixed(2) || "0.00"
    } ${currentGroup?.currency?.symbol || "€"}/${t(item.unit) || ""})`,
  }));


  const onServiceGroupChange = (e) => {
    const option = serviceGroupOptions.find((opt) => opt.value === e.value);
    setSelectedServiceGroup(option || null);
    setSelectedImpactedService(null);
  };

  const onImpactedServiceChange = (e) => {
    setSelectedImpactedService(e);
  };

  const onSiteSelect = (_, newValue) => {
    setSelectedSite(newValue);
    setSelectedImpactedService(null);
  };

  useEffect(() => {
    if (effectiveSiteId) {
      setShowSiteError(false);
    }
  }, [effectiveSiteId]);

  const canAdd = !!selectedServiceGroup && !!selectedImpactedService;

  const handleAddLine = () => {
    if (!effectiveSiteId) {
      setShowSiteError(true);
      return;
    }
    const serviceItems = currentGroup?.quoteitems || currentGroup?.items || [];
    const quoteItem = serviceItems.find(
      (item) => item.id === selectedImpactedService?.value
    );
    if (!quoteItem || !currentGroup) return;
    onAddLine({
      quoteItem,
      quoteService: currentGroup,
      siteId: effectiveSiteId,
      siteName: effectiveSiteName,
      siteOpportunityNumber: effectiveOpportunityNumber,
    });
  };

  return (
    <ItemSelectorBox>
      <ItemSelectorLabel>
        {t("PURCHASE_ORDER_SELECT_SERVICE_HELP")}
      </ItemSelectorLabel>
      <ItemSelectorRow column="1.2fr 1.4fr 1.4fr auto auto">
        <div>
          <ContainerLabel required>{t("SITE")}</ContainerLabel>
          {context === "site" && (
            <ReadonlyField>
              {siteId
                ? `${siteId} - ${siteName || ""}`
                : siteName || ""}
            </ReadonlyField>
          )}
          {context === "company" && (
            <Autocomplete
              id="po-site-search"
              options={siteOptions || []}
              getOptionLabel={(option) =>
                option.id
                  ? `${option.id} - ${option.name || ""}`
                  : option.name || ""
              }
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("PURCHASE_ORDER_SEARCH_SITE")}
                  size="small"
                  style={autoSearchInputStyles}
                  onChange={(e) => setSiteQuery(e.target.value || undefined)}
                  InputProps={{
                    ...params.InputProps,
                    style: { height: "40px" }
                  }}
                />
              )}
              value={selectedSite || null}
              onChange={onSiteSelect}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, params) => {
                const filtered = createFilterOptions()(options, params);
                return [...filtered];
              }}
            />
          )}
        </div>
        <Dropdown
          id="po-service-group"
          label={t("PURCHASE_ORDER_SERVICE_GROUP")}
          name="po-service-group"
          value={selectedServiceGroup?.value}
          onChange={onServiceGroupChange}
          defaultOption={t("PURCHASE_ORDER_SERVICE_GROUP")}
          options={serviceGroupOptions}
          disabled={!effectiveSiteId || loadingServices}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          required
        />
        <Dropdown
          id="po-impacted-service"
          label={t("PURCHASE_ORDER_IMPACTED_SERVICE")}
          name="po-impacted-service"
          value={selectedImpactedService?.value}
          onChange={onImpactedServiceChange}
          defaultOption={t("PURCHASE_ORDER_IMPACTED_SERVICE")}
          options={impactedServiceOptions}
          disabled={!selectedServiceGroup}
          labelStyles={labelStyles}
          inputStyles={inputStyles}
          required
        />
        <AddLineButtonWrapper title={t("PURCHASE_ORDER_ADD_LINE_TOOLTIP")}>
          <AddCircleButton
            onClick={handleAddLine}
            disabled={!canAdd}
            type="button"
          >
            {loadingServices ? <ClipLoader size={13} color="#fff" /> : "+"}
          </AddCircleButton>
        </AddLineButtonWrapper>
        <FreeLineLink
          onClick={() => {
            if (context === "site" && !effectiveSiteId) {
              setShowSiteError(true);
              return;
            }
            setShowSiteError(false);
            onAddFreeLine({
              siteId: context === "site" ? effectiveSiteId : null,
              siteName: context === "site" ? effectiveSiteName : "",
              siteOpportunityNumber:
                context === "site" ? effectiveOpportunityNumber : null,
            });
          }}
        >
          {t("PURCHASE_ORDER_ADD_FREE_LINE")}
        </FreeLineLink>
      </ItemSelectorRow>
      {showSiteError && !effectiveSiteId && (
        <ErrorText>{t("FIELD_REQUIRED")}</ErrorText>
      )}
    </ItemSelectorBox>
  );
};

PurchaseOrderItemSelector.propTypes = {
  t: PropTypes.func.isRequired,
  context: PropTypes.oneOf(["site", "company"]).isRequired,
  supplierId: PropTypes.number,
  siteId: PropTypes.number,
  siteName: PropTypes.string,
  siteOpportunityNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchCountryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSite: PropTypes.object,
  setSelectedSite: PropTypes.func,
  onAddLine: PropTypes.func.isRequired,
  onAddFreeLine: PropTypes.func.isRequired,
};

PurchaseOrderItemSelector.defaultProps = {
  siteId: null,
  siteName: "",
  siteOpportunityNumber: null,
  searchCountryId: null,
  selectedSite: null,
  setSelectedSite: () => {},
};

export default withTranslation()(PurchaseOrderItemSelector);
